import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AdminDecision, ModuleId } from "@wato/business-types";
import { useApi } from "../../api/context";
import { useOptionalWorkspace } from "./WorkspaceContext";

/**
 * OWN-333: module flags only (no request). `flags` already holds the admin decision (the shell
 * copies the resolved `enabled` map); a missing flag means on, as before.
 */
export function useModuleFlags() {
  const flags = useOptionalWorkspace()?.workspace.modules;
  const isOn = useCallback((id: ModuleId): boolean => flags?.[id] !== false, [flags]);
  return { flags: flags ?? {}, isOn };
}

/** Flags plus the admin's `locks` ("on" = locked on, "off" = hidden from the owner). */
export function useModules() {
  const api = useApi();
  const workspaceId = useOptionalWorkspace()?.workspace.id ?? null;
  const { flags, isOn } = useModuleFlags();
  const locksQuery = useQuery({
    queryKey: ["workspace-modules", workspaceId],
    queryFn: () => api.getWorkspaceModules(workspaceId as string),
    enabled: workspaceId !== null,
    staleTime: 60_000,
  });
  const locks: Partial<Record<ModuleId, AdminDecision>> = locksQuery.data?.locks ?? {};
  return {
    flags,
    locks,
    /** False until the first locks answer (success or error), so guards don't flash. */
    ready: !locksQuery.isPending,
    isOn,
    isHiddenByAdmin: (id: ModuleId): boolean => locks[id] === "off",
  };
}
